import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Popover,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DEFAULT_QUERY_PARAMS } from "../../utils/queryBuilder";

const DEFAULT_MIN = DEFAULT_QUERY_PARAMS.startYear;
const DEFAULT_MAX = DEFAULT_QUERY_PARAMS.endYear;

const makeClamp = (min, max) => (value, fallback) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return fallback;
  return Math.min(max, Math.max(min, next));
};

const QueryYearPopover = ({ anchorEl, startYear, endYear, min, max, onClose, onCommit }) => {
  const yearMin = min ?? DEFAULT_MIN;
  const yearMax = max ?? DEFAULT_MAX;
  const clampYear = makeClamp(yearMin, yearMax);
  const open = Boolean(anchorEl);
  const wasOpenRef = useRef(false);
  const [draftStart, setDraftStart] = useState(startYear);
  const [draftEnd, setDraftEnd] = useState(endYear);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraftStart(clampYear(startYear, yearMin));
      setDraftEnd(clampYear(endYear, yearMax));
    }
    wasOpenRef.current = open;
  }, [open, startYear, endYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRangeChange = (_, value) => {
    setDraftStart(value[0]);
    setDraftEnd(value[1]);
  };

  const handleStartChange = (event) => {
    const next = clampYear(event.target.value, draftStart);
    setDraftStart(next);
    if (next > draftEnd) setDraftEnd(next);
  };

  const handleEndChange = (event) => {
    const next = clampYear(event.target.value, draftEnd);
    setDraftEnd(next);
    if (next < draftStart) setDraftStart(next);
  };

  const handleApply = () => {
    onCommit({ startYear: draftStart, endYear: draftEnd });
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          mt: 1,
          width: { xs: 320, sm: 380 },
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
          Season range
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Limit season-based queries by year.
        </Typography>

        <Box sx={{ px: 1.25, pt: 3, pb: 1 }}>
          <Slider
            value={[draftStart, draftEnd]}
            min={yearMin}
            max={yearMax}
            onChange={handleRangeChange}
            valueLabelDisplay="auto"
            disableSwap
          />
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            type="number"
            label="From"
            value={draftStart}
            onChange={handleStartChange}
            inputProps={{ min: yearMin, max: yearMax }}
            sx={{ flex: 1 }}
          />
          <Typography color="text.secondary">to</Typography>
          <TextField
            size="small"
            type="number"
            label="To"
            value={draftEnd}
            onChange={handleEndChange}
            inputProps={{ min: yearMin, max: yearMax }}
            sx={{ flex: 1 }}
          />
        </Stack>
      </Box>

      <Divider />

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 1.5 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </Stack>
    </Popover>
  );
};

export default QueryYearPopover;
